<script lang="ts">
  import {DropdownButton} from '@/components/button';
  import {getDownloadOptionsContext} from '@/contexts/download';
  import {isMobile, user} from '@/store';
  import type {Video} from '@/types/project';
  import {download} from '@/utils/common';
  import {httpClient} from '@/utils/http-client';
  import {onMount} from 'svelte';
  import {parseBlogStyle} from './Blogs';

  export let selectedVideo: Video | null;

  const type = 'Blog';
  const downloadOptions = getDownloadOptionsContext();

  let isLoadingBlog = true;
  let blogStyle: {headerSize: number; content: string}[] = [];
  let textBlog = '';
  let warningMessage: string = '';

  $: isDownloadable = !isLoadingBlog || textBlog !== '';

  async function regenerate() {
    if (!selectedVideo) throw 'selectedVideo is null';
    isLoadingBlog = true;
    blogStyle = [];
    try {
      const response = await httpClient
        .get(`/sermon-summary/regenerate/${selectedVideo.id}`)
        .then(response => response.data);
      const text = response.result?.blog || '';
      if (typeof text !== 'string') throw 'subtitle text is not a string.';

      textBlog = text;
      blogStyle = parseBlogStyle(text);
    } catch (error) {
      console.error(error);
    }

    isLoadingBlog = false;
  }

  const handleDownload = async (extension: 'txt' | 'html' | 'pdf' = 'txt') => {
    const filename = `[${type}] ${selectedVideo?.title}`;
    await download(filename, textBlog, type, extension);
  };

  onMount(async () => {
    if (!selectedVideo) throw 'selectedVideo is null';

    try {
      const response = await httpClient.get(`/sermon-summary/${selectedVideo.id}`).then(response => response.data);
      const text = response.result?.blog || '';
      if (typeof text !== 'string') throw 'subtitle text is not a string.';

      textBlog = text;
      blogStyle = parseBlogStyle(text);
    } catch (error) {
      if (selectedVideo?.metadata && selectedVideo?.metadata?.duration < 300) {
        warningMessage =
          "The video you've uploaded is too short for our content generation system to work effectively. Please upload a video that's at least 5 minutes long to get AI-powered content.";
      } else {
        warningMessage = 'Failed to Generate Devotionals.';
      }
      console.error(error);
    }

    isLoadingBlog = false;
  });
</script>

<div class="h-full w-full">
  <div class="flex h-16 w-full items-center justify-between border-b border-b-gray-200 px-4 dark:border-b-zinc-600">
    {#if !$isMobile}
      <h2 class="text-md w-full font-bold text-gray-900">Blog</h2>
    {/if}
    <div class="flex w-full justify-center space-x-2 py-2 lg:justify-end lg:py-0">
      {#if $user.isImpersonated || $user.isAdmin}
        <button
          type="button"
          on:click={() => regenerate()}
          class="w-full rounded-md px-3 py-1 lg:w-auto {isLoadingBlog
            ? 'disabled cursor-progress bg-zinc-400 text-white'
            : 'hoversermonbg border border-primary bg-white text-primary hover:bg-primary/10'}"
          ><i class="fa-solid fa-repeat mr-2" />Regenerate</button
        >
      {/if}
      <DropdownButton
        on:click={() => handleDownload()}
        on:optionClick={option => handleDownload(option.detail.value)}
        disabled={!isDownloadable}
        options={downloadOptions}
      >
        <span class="flex items-center gap-x-2">
          <i class="fa-solid fa-download" />Download
        </span>
      </DropdownButton>
    </div>
  </div>
  <div class="h-[calc(100%-4rem)] w-full overflow-auto p-4">
    {#if isLoadingBlog}
      <div class="flex h-full w-full justify-center">
        <div class="flex flex-col items-center gap-4 pt-10">
          <div
            class="spinner h-8 w-8 !border-zinc-200 !border-t-rose-500 dark:!border-zinc-700 dark:!border-t-rose-500"
          />
          <h1 class="text-3xl font-bold text-zinc-400 dark:text-zinc-400">Loading Blog Style...</h1>
        </div>
      </div>
    {:else if blogStyle.length > 0}
      <div class="flex h-full w-full justify-center">
        <div class="h-full w-full max-w-[40rem]">
          <div id="blogcontent" class="grid pb-16">
            {#each blogStyle as blog, i}
              {#if blog.headerSize === 0}
                {#if blog.content.includes('/ul') || blog.content.includes('/ol')}
                  <br />
                {:else}
                  <p class="text-justify leading-loose text-dark-400">
                    {@html blog.content}
                  </p>
                {/if}
              {:else if blog.headerSize === 1}
                <span class="pb-6 font-primary text-xl font-bold text-dark lg:text-4xl">{@html blog.content}</span>
              {:else}
                <span class="font-primary font-bold text-dark {i !== 0 ? 'pb-4 pt-10' : 'pb-6'}"
                  >{@html blog.content}</span
                >
              {/if}
            {/each}
          </div>
        </div>
      </div>
    {:else}
      <div class="flex h-full w-full items-center justify-center">
        <div class="flex flex-col items-center justify-center gap-4">
          <h1 class="pt-10 text-3xl font-bold text-zinc-400 dark:text-zinc-400">No Blog Style Found</h1>
          <p class="text-zinc-400 dark:text-zinc-400">{warningMessage}</p>
        </div>
      </div>
    {/if}
  </div>
</div>
